import { parseDateOnly } from './parse';

// gloss
// why: Instant vs date-only values need different day formatters; using formatLocalDay on a
// why: UTC-midnight date shifts it a day earlier west of UTC.
const pad = (value: number): string => String(value).padStart(2, '0');

const parse = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// gloss
export const formatLocalDay = (value: string | Date | null | undefined): string | null => {
  const date = parse(value);
  if (!date) return null;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// gloss
export const formatUtcDay = (value: string | Date | null | undefined): string | null => {
  const date = parse(value);
  if (!date) return null;
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};

// gloss
export const pickedDayToUtc = (value: string | Date | null | undefined): string | null => {
  const day = formatLocalDay(typeof value === 'string' ? parseDateOnly(value) : value);
  return day && `${day}T00:00:00.000Z`;
};

// gloss
export const pickedDayToInstant = (value: Date | null | undefined, now: Date = new Date()): string | null => {
  if (!value || Number.isNaN(value.getTime())) return null;
  const noon = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
  return (noon > now ? now : noon).toISOString();
};

// gloss
// why: takes a calendar day, not an instant — projecting 'YYYY-MM-DD' through a zone renders the day
// why: before it west of UTC, which is the shift formatLocalDay/formatUtcDay exist to resolve.
export const formatDayLabel = (
  day: string | null | undefined,
  { withYear = false }: { withYear?: boolean } = {},
): string | null => {
  if (!day) return null;

  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return null;

  return new Intl.DateTimeFormat('en-US', {
    ...(withYear ? ({ year: 'numeric' } as const) : ({ weekday: 'short' } as const)),
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, date)));
};
